/**
 * Money utilities — all amounts are integers in minor units (halalas for SAR).
 * 1 SAR = 100 halalas. Never use floats for money.
 */

const CURRENCY_DECIMALS: Record<string, number> = {
  SAR: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JOD: 3,
};

/** Format minor-unit amount for display. e.g. formatMoney(9900, 'SAR') → '99.00 SAR' */
export function formatMoney(amountMinorUnits: number, currency: string, locale = 'en'): string {
  const decimals = CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
  const majorUnits = amountMinorUnits / Math.pow(10, decimals);

  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(majorUnits);
}

/** Convert major-unit decimal string to minor units (integer). e.g. '99.00' → 9900 */
export function parseMoney(majorUnits: string | number, currency: string): number {
  const decimals = CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
  const factor = Math.pow(10, decimals);
  const val = typeof majorUnits === 'string' ? parseFloat(majorUnits) : majorUnits;
  if (isNaN(val)) throw new Error(`Invalid money value: ${String(majorUnits)}`);
  return Math.round(val * factor);
}

/** Convert minor units to major units for display inputs. e.g. 9900 → 99 */
export function minorToMajor(amountMinorUnits: number, currency: string): number {
  const decimals = CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
  return amountMinorUnits / Math.pow(10, decimals);
}
